import { Component, inject, ViewEncapsulation } from '@angular/core';
import { RouterOutlet, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { HeaderComponent } from './components/header/header-component';
import { ChatLayoutProvider } from './providers/chat-layout-provider';
import { LucideMessageSquarePlus, LucideSettings, LucidePanelLeftClose, LucidePanelLeft } from '@lucide/angular';

@Component({
  selector: 'app-layout-chat',
  standalone: true,
  imports: [RouterOutlet, CommonModule, HeaderComponent, RouterLink, LucideMessageSquarePlus, LucideSettings, LucidePanelLeftClose, LucidePanelLeft],
  template: `
    <app-header></app-header>
    <div class="lcv-root">
      <!-- ═══════ SIDEBAR (rail when closed, full when open) ═══════ -->
      <aside class="lcv-sidebar" [class.closed]="!layout.state().sidebarOpen" aria-label="Side navigation">

        <!-- Top: toggle button -->
        <div class="lcv-sb-top-row">
          <button class="lcv-sb-toggle" (click)="layout.notifier.toggleSidebar()" [attr.aria-label]="layout.state().sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'">
            @if (layout.state().sidebarOpen) {
              <svg lucidePanelLeftClose size="18"></svg>
            } @else {
              <svg lucidePanelLeft size="18"></svg>
            }
          </button>
        </div>

        <!-- New chat button -->
        <a class="lcv-sb-item" routerLink="/chat/new" (click)="layout.notifier.toggleSidebar()" title="New chat">
          <svg lucideMessageSquarePlus size="18" class="lcv-sb-icon"></svg>
          @if (layout.state().sidebarOpen) {
            <span class="lcv-sb-label">New chat</span>
          }
        </a>

        <!-- Scrollable middle -->
        <div class="lcv-sb-scroll">
          @if (layout.state().sidebarOpen) {
            <div class="lcv-sb-history-label">Recent</div>
          }
        </div>

        <!-- Settings -->
        <button class="lcv-sb-item lcv-sb-footer-item" title="Settings">
          <svg lucideSettings size="18" class="lcv-sb-icon"></svg>
          @if (layout.state().sidebarOpen) {
            <span class="lcv-sb-label">Settings</span>
          }
        </button>

      </aside>

      <!-- ═══════ MAIN CONTENT ═══════ -->
      <main class="lcv-main">
        <router-outlet></router-outlet>
      </main>
    </div>
  `,
  styles: [`
    :host {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      overflow: hidden;
      background: var(--op-bg-deep);
      color: var(--op-text-hi);
    }

    .lcv-root {
      flex: 1;
      display: flex;
      min-height: 0;
      overflow: hidden;
    }

    /* ═══════════════════════════════════════════════════
       SIDEBAR — rail when closed (52px), panel when open (288px)
       ═══════════════════════════════════════════════════ */
    .lcv-sidebar {
      width: 52px;
      min-width: 52px;
      display: flex;
      flex-direction: column;
      background: var(--op-bg);
      border-right: 1px solid var(--op-border);
      transition: width 0.2s ease, min-width 0.2s ease;
      overflow: hidden;
      z-index: 5;
    }

    .lcv-sidebar.closed {
      width: 52px;
      min-width: 52px;
    }

    .lcv-sidebar:not(.closed) {
      width: 288px;
      min-width: 288px;
    }

    .lcv-sb-top-row {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 8px;
      min-height: 52px;
    }

    .lcv-sidebar:not(.closed) .lcv-sb-top-row {
      justify-content: flex-end;
    }

    .lcv-sb-toggle {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      border: none;
      background: transparent;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      color: var(--op-text-dim);
      transition: background 0.15s, color 0.15s;
    }

    .lcv-sb-toggle:hover {
      background: var(--op-bg-hover);
      color: var(--op-text-hi);
    }

    /* ── Sidebar items ───────────────── */
    .lcv-sb-item {
      display: flex;
      align-items: center;
      gap: 14px;
      margin: 2px 8px;
      padding: 10px;
      border-radius: 8px;
      border: none;
      background: transparent;
      color: var(--op-text-hi);
      font-size: 14px;
      font-weight: 500;
      font-family: inherit;
      text-decoration: none;
      cursor: pointer;
      transition: background 0.15s;
      white-space: nowrap;
      overflow: hidden;
    }

    .lcv-sb-item:hover {
      background: var(--op-bg-hover);
    }

    .lcv-sb-icon {
      flex-shrink: 0;
    }

    .lcv-sb-label {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    /* ── New chat ────────────────────── */
    .lcv-sb-item:has(.lcv-sb-icon) {
      justify-content: center;
    }

    .lcv-sidebar:not(.closed) .lcv-sb-item:has(.lcv-sb-icon) {
      justify-content: flex-start;
    }

    .lcv-sidebar:not(.closed) .lcv-sb-top-row {
      justify-content: flex-end;
    }

    /* ── Scrollable middle ───────────── */
    .lcv-sb-scroll {
      flex: 1;
      overflow-y: auto;
      padding: 4px 0;
    }

    .lcv-sb-history-label {
      font-size: 11px;
      font-weight: 600;
      color: var(--op-text-dim);
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 12px 12px 4px;
    }

    /* ── Footer (Settings) ──────────── */
    .lcv-sb-footer-item {
      margin: 2px 8px 8px;
      justify-content: center;
    }

    .lcv-sidebar:not(.closed) .lcv-sb-footer-item {
      justify-content: flex-start;
    }

    /* ═══════════════════════════════════════════════════
       MAIN CONTENT
       ═══════════════════════════════════════════════════ */
    .lcv-main {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .lcv-main > router-outlet {
      display: none;
    }

    .lcv-main > router-outlet + * {
      flex: 1;
      display: flex;
      flex-direction: column;
      min-height: 0;
    }
  `],
  encapsulation: ViewEncapsulation.Emulated,
})
export class LayoutChatView {
  protected readonly layout = inject(ChatLayoutProvider);
}
